import type { CeoDailyPaymentsSummary } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { formatDateRu, formatMillions, formatMoney } from '../ceoFormat';
import { SplitRow } from '../ui';
import {
  BarFill,
  BarTrack,
  BreakdownTitle,
  CardSubtext,
  CategoryBreakdown,
  CategoryRow,
  CounterpartyText,
  InflowBold,
  ItemInfo,
  OperationsList,
  OutflowBold,
  PaymentAmount,
  PaymentRow,
  PurposeEllipsis,
  SectionHeaderMb,
  SectionLink,
  SectionTitle,
  TopOperationsTitle,
} from './styles';

interface YesterdayPaymentsCardProps {
  yPay: CeoDailyPaymentsSummary;
}

export function YesterdayPaymentsCard({ yPay }: YesterdayPaymentsCardProps) {
  return (
    <Card>
      <SectionHeaderMb>
        <div>
          <SectionTitle>
            Платежи за прошлый день ({formatDateRu(yPay.report_date)})
          </SectionTitle>
          <CardSubtext>
            Входящий: {formatMillions(yPay.opening_balance)} → Приход:{' '}
            <InflowBold>
              +{formatMillions(yPay.inflow_total)}
            </InflowBold>{' '}
            → Расход:{' '}
            <OutflowBold>
              −{formatMillions(yPay.outflow_total)}
            </OutflowBold>{' '}
            → Исходящий: <strong>{formatMillions(yPay.closing_balance)}</strong>
            {yPay.internal_transfer_count > 0 && (
              <span>
                {' '}
                · Межсчет. переводы: {formatMillions(yPay.internal_transfer_total)}
              </span>
            )}
          </CardSubtext>
        </div>
        <SectionLink to="/agents/ceo/accounts">Реестр платежей →</SectionLink>
      </SectionHeaderMb>

      <CategoryBreakdown>
        <BreakdownTitle>
          Структура списаний за прошлый день ({yPay.outflow_count} п/п на{' '}
          {formatMoney(yPay.outflow_total)}):
        </BreakdownTitle>
        {yPay.outflow_by_category.map((cat) => (
          <CategoryRow key={cat.category}>
            <SplitRow>
              <span>
                {cat.category_label} ({cat.payments_count} п/п)
              </span>
              <strong>
                {formatMoney(cat.amount)} ({cat.share_pct}%)
              </strong>
            </SplitRow>
            <BarTrack>
              <BarFill $pct={cat.share_pct} />
            </BarTrack>
          </CategoryRow>
        ))}
      </CategoryBreakdown>

      <TopOperationsTitle>
        Крупнейшие операции за {formatDateRu(yPay.report_date)}:
      </TopOperationsTitle>
      <OperationsList>
        {yPay.payments.slice(0, 5).map((p) => (
          <PaymentRow key={p.id}>
            <ItemInfo>
              <CounterpartyText>{p.counterparty_name}</CounterpartyText>
              <PurposeEllipsis>{p.purpose}</PurposeEllipsis>
            </ItemInfo>
            <PaymentAmount $inflow={p.direction === 'inflow'}>
              {p.direction === 'inflow' ? '+' : '−'}
              {formatMoney(p.amount)}
            </PaymentAmount>
          </PaymentRow>
        ))}
      </OperationsList>
    </Card>
  );
}
